import React from 'react';
import { Plus, Loader2, Trash2, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AIConversation } from '@/hooks/useAIAssistant';

interface Props {
  conversations: AIConversation[];
  loading: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function ConversationList({ conversations, loading, onSelect, onNew, onDelete }: Props) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={20} className="animate-spin text-[#9a9aac]" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-3">
        <button
          onClick={onNew}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-iris-600/10 border border-iris-500/20 text-iris-400 hover:bg-iris-600/20 transition-colors text-sm font-medium"
        >
          <Plus size={16} />
          {t('aiAssistant.newChat')}
        </button>
      </div>

      {conversations.length === 0 && (
        <div className="text-center py-12 px-4">
          <MessageSquare size={32} className="mx-auto mb-3 text-[#9a9aac]" />
          <p className="text-sm text-[#9a9aac]">{t('aiAssistant.noConversations')}</p>
        </div>
      )}

      <div className="px-2 space-y-0.5">
        {conversations.map(conv => (
          <div
            key={conv.id}
            onClick={() => onSelect(conv.id)}
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer hover:bg-white/4 transition-colors group"
          >
            <MessageSquare size={14} className="text-[#9a9aac] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{conv.title || 'Untitled'}</p>
              <p className="text-[11px] text-[#9a9aac]">
                {conv.message_count ? `${conv.message_count} msgs` : ''} {timeAgo(conv.updated_at)}
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(conv.id); }}
              className="p-1 rounded text-[#9a9aac] hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
